import { Link } from "react-router-dom";
import { Instagram, MapPin, Phone, Mail, Facebook, Youtube } from "lucide-react";
import { NAV_LINKS, SITE } from "../data/site";
import { useSettings, useServiceAreas } from "../hooks/useCms";

const Footer = () => {
  const settings = useSettings();
  const areas = useServiceAreas();

  const phone = settings.phone || SITE.phone;
  const email = settings.email || SITE.email;
  const instagram = settings.instagram || SITE.instagram;
  const instagramHandle = settings.instagram_handle || SITE.instagramHandle;
  const region = settings.region || SITE.region;
  const businessName = settings.business_name || SITE.name;
  const links = settings.nav?.length ? settings.nav.filter((n) => n.visible !== false) : NAV_LINKS;
  const areaNames = areas.length ? areas.map((a) => ({ label: a.name, to: `/areas/${a.slug}` })) : [];

  return (
    <footer data-testid="site-footer" className="bg-[#1A1A1A] text-[#F7F5F2]">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8 pb-16 border-b border-white/10">
          <div className="md:col-span-5">
            <img src="/logo.webp" alt="On Point Homes logo" className="h-40 w-auto invert mb-8" />
            <p className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Built With Intention.<br />Built For You.
            </p>
            {settings.footer_text && (
              <p className="mt-6 text-[#A3A09B] text-sm leading-relaxed max-w-sm">{settings.footer_text}</p>
            )}
          </div>
          <div className="md:col-span-3">
            <p className="eyebrow text-[#A3A09B] mb-6">Explore</p>
            <nav className="flex flex-col gap-3" aria-label="Footer">
              {links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`footer-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="link-underline w-fit text-sm text-[#F7F5F2]/85 hover:text-white transition-colors duration-300"
                >
                  {l.label}
                </Link>
              ))}
            </nav>
            {areaNames.length > 0 && (
              <>
                <p className="eyebrow text-[#A3A09B] mt-8 mb-4">Where We Build</p>
                <nav className="grid grid-cols-2 gap-x-6 gap-y-3" aria-label="Service areas">
                  {areaNames.map((a) => (
                    <Link
                      key={a.to}
                      to={a.to}
                      data-testid={`footer-area-${a.label.toLowerCase().replace(/\s/g, "-")}`}
                      className="link-underline w-fit text-sm text-[#F7F5F2]/85 hover:text-white transition-colors duration-300"
                    >
                      {a.label}
                    </Link>
                  ))}
                </nav>
              </>
            )}
          </div>
          <div className="md:col-span-4">
            <p className="eyebrow text-[#A3A09B] mb-6">Get In Touch</p>
            <div className="flex flex-col gap-4 text-sm text-[#F7F5F2]/85">
              <span className="flex items-center gap-3"><MapPin size={15} className="text-[#8C6A4F]" /> {region}</span>
              {settings.address && <span className="flex items-center gap-3 text-[#A3A09B]">{settings.address}</span>}
              {settings.hours && <span className="flex items-center gap-3 text-[#A3A09B]">{settings.hours}</span>}
              <a href={`tel:${phone}`} data-testid="footer-phone" className="flex items-center gap-3 hover:text-white transition-colors duration-300"><Phone size={15} className="text-[#8C6A4F]" /> {phone}</a>
              <a href={`mailto:${email}`} data-testid="footer-email" className="flex items-center gap-3 hover:text-white transition-colors duration-300"><Mail size={15} className="text-[#8C6A4F]" /> {email}</a>
              <a href={instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="flex items-center gap-3 hover:text-white transition-colors duration-300"><Instagram size={15} className="text-[#8C6A4F]" /> {instagramHandle}</a>
              {settings.facebook && (
                <a href={settings.facebook} target="_blank" rel="noreferrer" data-testid="footer-facebook" className="flex items-center gap-3 hover:text-white transition-colors duration-300"><Facebook size={15} className="text-[#8C6A4F]" /> Facebook</a>
              )}
              {settings.youtube && (
                <a href={settings.youtube} target="_blank" rel="noreferrer" data-testid="footer-youtube" className="flex items-center gap-3 hover:text-white transition-colors duration-300"><Youtube size={15} className="text-[#8C6A4F]" /> YouTube</a>
              )}
            </div>
          </div>
        </div>
        <div className="pt-8 flex flex-col md:flex-row justify-between gap-3 items-start md:items-center">
          <p className="font-mono text-[0.62rem] tracking-[0.3em] uppercase text-[#A3A09B]">{businessName} — Idaho Home Builder</p>
          <p className="font-mono text-[0.62rem] tracking-[0.3em] uppercase text-[#A3A09B]">© {new Date().getFullYear()} All Rights Reserved</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
